import type { ArticleConceptLink, EconomicArticle, LearningChapter, LearningProgress, LearningScene } from './models';

export function publishedChapters(chapters: LearningChapter[]) {
  return chapters.filter((chapter) => chapter.status === 'published').sort((a,b) => a.order - b.order);
}

export function chapterScenes(chapter: LearningChapter, scenes: LearningScene[]) {
  return scenes.filter((scene) => scene.chapterId === chapter.id && chapter.sceneIds.includes(scene.id)).sort((a,b) => a.order-b.order);
}

export function chapterProgress(chapter: LearningChapter, progress?: LearningProgress) {
  if (!chapter.sceneIds.length) return 0;
  return Math.min(100, Math.round(((progress?.completedSceneIds.length ?? 0) / chapter.sceneIds.length) * 100));
}

export function recommendedChapter(
  chapters: LearningChapter[],
  progress: LearningProgress[],
  links: ArticleConceptLink[],
  articles: EconomicArticle[]
) {
  const published = publishedChapters(chapters);
  const completed = new Set(progress.filter((item) => item.completed).map((item) => item.chapterId));
  const latestPublished = [...articles].filter((item) => item.status === 'published').sort((a,b)=>String(b.publishedAt ?? b.updatedAt).localeCompare(String(a.publishedAt ?? a.updatedAt)))[0];
  if (latestPublished) {
    const linked = links.filter((item) => item.articleId === latestPublished.id).sort((a,b)=>b.relevanceScore-a.relevanceScore || a.recommendedOrder-b.recommendedOrder);
    const linkedChapter = linked.map((item)=>published.find((chapter)=>chapter.id===item.chapterId && !completed.has(chapter.id))).find(Boolean);
    if (linkedChapter) return linkedChapter;
  }
  return published.find((chapter)=>!completed.has(chapter.id)) ?? published[0];
}

export function learningCompletionRate(chapters: LearningChapter[], progress: LearningProgress[]) {
  const published = publishedChapters(chapters);
  if (!published.length) return 0;
  const completed = progress.filter((item)=>item.completed && published.some((chapter)=>chapter.id===item.chapterId)).length;
  return Math.round((completed/published.length)*100);
}

export function completeChapterProgress(input: {
  chapter: LearningChapter;
  childId: string;
  existing?: LearningProgress;
  completedAt?: string;
}) : LearningProgress {
  const completedAt = input.completedAt ?? new Date().toISOString();
  return {
    id: input.existing?.id ?? `learning_${input.childId}_${input.chapter.id}`,
    childId: input.childId,
    chapterId: input.chapter.id,
    completedSceneIds: [...input.chapter.sceneIds],
    completed: true,
    xpEarned: input.chapter.xpReward,
    startedAt: input.existing?.startedAt ?? completedAt,
    completedAt
  };
}
